'use client';
import { memo, useMemo, useState } from 'react';

import { Table } from '../../table';
import { calcProfitabilityByType, niceNameByType } from './helpers';
import { IGeneralStats, marketData } from '@core/market-data-sdk';

import {
  getHistoricalProfitTableColumns,
  IHistoricalProfitTable,
  INDICATOR_TYPE_OPTIONS,
  IndicatorType,
} from './constants';
import { ITableProps } from '../../interface';
import useSWR from 'swr';
import { TableMaskType } from '../../core/table-creator';
import { SelectApp } from '../../../input/components/select/app';
import { BenchmarkApp as Benchmark } from '../../../chart/components/benchmarks/app';

interface IProps {
  userIsPrime?: boolean;
  assetName: string;
  stats: IGeneralStats | undefined;
  isLoadingStats: boolean;
  formatDecimal?: boolean; // Sent to BENCHMARK API to get values with or without decimal. If True, values will be 0.1 like 10%. If False, values will be 10 like 10%.
  tableRef?: React.RefObject<HTMLDivElement>;
  cardFooter?: React.ReactNode;
  tableConfigName?: string;
  cardProps?: React.HTMLAttributes<HTMLDivElement>;
  tableProps?: Partial<ITableProps>;
  defaultBenchmark?: string;
  customBenchmarks?: {
    label: string;
    value: string;
  }[];
  onUpdateCustomBenchmark?: (benchmark?: string) => void;
  customBenchmarksEnabled?: boolean;
}

export const HistoricalProfitabilityTable = memo<IProps>(
  ({
    userIsPrime = true,
    formatDecimal = true,
    assetName = 'Ativo',
    stats,
    isLoadingStats,
    tableRef,
    cardFooter,
    tableConfigName,
    cardProps,
    tableProps = {},
    defaultBenchmark = 'cdi',
    customBenchmarks = [],
    onUpdateCustomBenchmark = () => {},
    customBenchmarksEnabled = false,
  }) => {
    const [type, setType] = useState(IndicatorType.VALUE);
    const [selectedBenchmark, setSelectedBenchmark] =
      useState(defaultBenchmark);

    const { data: benchmarkStats, isLoading: benchmarkLoading } = useSWR(
      ['/stats', stats, selectedBenchmark],
      () =>
        !stats
          ? null
          : marketData.general.getStats({
              identifier: `${selectedBenchmark}:idx`,
              startDate: stats?.stats?.first_quote_date as any,
              endDate: stats?.stats?.last_quote_date as any,
              format_decimal: formatDecimal,
            })
    );

    const handleSelectBenchmark = (value: string) => {
      setSelectedBenchmark(value);

      switch (value) {
        case 'cdi':
          setType(IndicatorType.RELATIVE_PERCENTAGE);
          break;
        case 'ibov':
          setType(IndicatorType.PERCENTAGE_POINTS);
          break;
      }
    };

    const tableData = useMemo<IHistoricalProfitTable[]>(() => {
      if (!stats?.years) return [];

      let benchmarkNicename = '';

      if (selectedBenchmark.endsWith(':rfs') && benchmarkStats?.nicename) {
        benchmarkNicename = benchmarkStats.nicename;
      } else {
        benchmarkNicename = niceNameByType(type, selectedBenchmark);
      }

      return Object.entries(stats.years)
        .reverse()
        .map(([year, stats]) => {
          const statsByType = Object.entries(
            benchmarkStats?.years?.[year] || {}
          ).reduce(
            (total, [key, value]) => ({
              ...total,
              [key]: calcProfitabilityByType({
                type,
                asset: stats[key] as number,
                benchmark: value as number,
                formatDecimal,
              }),
            }),
            {}
          );

          return {
            yearName: year,
            nicename: assetName,
            ...stats,
            benchmarkNicename,
            benchmarkStatsYear: statsByType,
          } as IHistoricalProfitTable;
        });
    }, [stats, benchmarkStats, type, formatDecimal]);

    const columns = useMemo(() => {
      return getHistoricalProfitTableColumns(
        formatDecimal ? TableMaskType.PERCENT : TableMaskType.PERCENT_OTHER
      );
    }, [formatDecimal]);

    return (
      <Table
        id="historical-profitability-table"
        tableRef={tableRef}
        loading={isLoadingStats || benchmarkLoading}
        cardProps={{ className: 'my-8', ...cardProps }}
        title="Rentabilidade histórica"
        disableSort
        tableConfigName={tableConfigName}
        columns={columns}
        data={tableData}
        headerActions={
          <div className="flex gap-2 mb-2 ">
            <SelectApp
              startAdornment="Exibir:"
              size="small"
              value={type}
              onChange={(e: any) => {
                setType(e.target.value);
              }}
              options={INDICATOR_TYPE_OPTIONS}
              className="w-fit"
            />

            <Benchmark
              isSingle
              onSelectedBenchmarksChange={handleSelectBenchmark as any}
              userIsPrime={userIsPrime}
              defaultSelectedBenchmarks={[selectedBenchmark]}
              customBenchmarks={customBenchmarks}
              onUpdateCustomBenchmark={onUpdateCustomBenchmark}
              customBenchmarksEnabled={customBenchmarksEnabled}
            />
          </div>
        }
        cardFooter={cardFooter}
        {...tableProps}
      />
    );
  }
);

export default HistoricalProfitabilityTable;
